import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";

const GTM_SCRIPT_ID = "pt-gtm-script";
const GTM_NOSCRIPT_ID = "pt-gtm-noscript";
const GTM_DATA_LAYER = "dataLayer";

const normalizeRoute = (route = "") => {
  if (!route || route === "/") return "/";
  return route.endsWith("/") ? route.slice(0, -1) : route;
};

const isRouteMatched = (pathname, routeMatchArray) => {
  const currentPath = normalizeRoute(pathname);
  return routeMatchArray.some((route) => normalizeRoute(route) === currentPath);
};

const removeGTMNodes = () => {
  if (typeof document === "undefined") return;

  const scriptTag = document.getElementById(GTM_SCRIPT_ID);
  if (scriptTag?.parentNode) {
    scriptTag.parentNode.removeChild(scriptTag);
  }

  const noScriptTag = document.getElementById(GTM_NOSCRIPT_ID);
  if (noScriptTag?.parentNode) {
    noScriptTag.parentNode.removeChild(noScriptTag);
  }
};

const injectGTMScript = (gtmId) => {
  if (typeof document === "undefined") return;
  if (document.getElementById(GTM_SCRIPT_ID)) return;

  const script = document.createElement("script");
  script.id = GTM_SCRIPT_ID;
  script.async = true;
  script.text = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','${GTM_DATA_LAYER}','${gtmId}');
  `;
  document.head.appendChild(script);
};

const injectGTMNoScript = (gtmId) => {
  if (typeof document === "undefined" || !document.body) return;
  if (document.getElementById(GTM_NOSCRIPT_ID)) return;

  const noScript = document.createElement("noscript");
  noScript.id = GTM_NOSCRIPT_ID;
  noScript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

  document.body.insertAdjacentElement("afterbegin", noScript);
};

export default function useGTM(routeMatchArray = [], gtmId = "GTM-MWT26QW3") {
  const location = useLocation();

  const shouldEnableGTM = useMemo(
    () => isRouteMatched(location.pathname, routeMatchArray),
    [location.pathname, routeMatchArray]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !gtmId) return undefined;

    if (shouldEnableGTM) {
      injectGTMScript(gtmId);
      injectGTMNoScript(gtmId);
    } else {
      removeGTMNodes();
    }

    return () => {
      removeGTMNodes();
    };
  }, [shouldEnableGTM, gtmId]);
}
